import React from "react";
import { cn } from "@/lib/utils";

interface TabItem {
  id: string;
  label: string;
  content?: React.ReactNode;
}

interface TabProps {
  items?: TabItem[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
  className?: string;
  // Legacy props for backward compatibility
  tabs?: TabItem[];
  size?: "sm" | "md" | "lg";
}

// Main Tab component
const TabComponent = React.forwardRef<HTMLDivElement, TabProps>(
  (
    { items, tabs, activeTab, onTabChange, className, size = "md", ...props },
    ref,
  ) => {
    // Support both items and tabs props for backward compatibility
    const tabItems = items || tabs || [];

    const getSizeClasses = (s: "sm" | "md" | "lg") => {
      switch (s) {
        case "sm":
          return { height: "h-8", text: "text-xs" };
        case "lg":
          return { height: "h-10", text: "text-base" };
        default:
          return { height: "h-9", text: "text-sm" };
      }
    };

    const { height: heightClass, text: textSizeClass } = getSizeClasses(size);

    return (
      <div
        ref={ref}
        className={cn(
          // Responsive container: full width, center, allow horizontal scroll on small screens
          "w-full flex overflow-x-auto no-scrollbar",
          heightClass,
          className,
        )}
        {...props}
      >
        {tabItems.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
              className={cn(
                // Each tab is flexible and keeps minimum width for readability
                "flex-1 shrink-0 h-full flex items-center justify-center relative whitespace-nowrap",
                textSizeClass,
                "transition-colors",
                // Active state: yellow text and border
                isActive
                  ? "text-Color-Text-brand-default font-semibold border-b-2 border-Color-Stroke-brand"
                  : "text-Color-Text-neutral-supplement font-semibold border-b border-Color-Stroke-neutral",
              )}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
    );
  },
);

TabComponent.displayName = "Tab";

export { TabComponent as Tab, type TabItem, type TabProps };
